import React, { forwardRef, useState, useImperativeHandle } from 'react';
import { 
  TextField, 
  Typography,
  Grid,
  Box,
  useTheme,
  useMediaQuery 
} from '@mui/material';
import PropTypes from 'prop-types';
import { TextMaskZipCode } from './TextMaskCustom';

const COLORS = {
  primary: '#012F47',
  secondary: '#239CC4',
  white: '#FFFFFF',
};

const commonBoxStyles = {
  maxWidth: { xs: '100%', lg: '1200px' },
  mx: 'auto',
  bgcolor: COLORS.white,
  borderRadius: { xs: 2, sm: 3, md: 4 },
  boxShadow: '0px 6px 24px 0px #012F473A',
};

const AddressForm = forwardRef((props, ref) => {
  const { address, setAddress, errors, setErrors } = props;

  const validateCep = (zip_code) => {
    const numericCep = zip_code.replace(/\D/g, '');
    return numericCep.length === 8;
  };

  const validate = () => {
    const newErrors = {};
    
    if (!address.zip_code) {
      newErrors.zip_code = 'CEP é obrigatório';
    } else if (!validateCep(address.zip_code)) {
      newErrors.zip_code = 'CEP deve conter 8 dígitos';
    }

    if (!address.street) newErrors.street = 'Rua é obrigatória';
    if (!address.number) newErrors.number = 'Número é obrigatório';
    if (!address.neighborhood) newErrors.neighborhood = 'Bairro é obrigatório';
    if (!address.city) newErrors.city = 'Cidade é obrigatória';
    if (!address.state) newErrors.state = 'Estado é obrigatório';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useImperativeHandle(ref, () => ({
    validate
  }));

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const clearAddressFields = () => {
    setAddress(prev => ({
      ...prev,
      street: '',
      neighborhood: '',
      city: '',
      state: '',
    }));
  };

  const fetchAddress = async (zipCode) => {
    try {      
      const response = await fetch(`https://viacep.com.br/ws/${zipCode}/json/`);
      const data = await response.json();

      if (!data.erro) {
        setAddress(prev => ({
          ...prev,
          street: data.logradouro,
          neighborhood: data.bairro,
          city: data.localidade,
          state: data.uf,
        }));
        
        setErrors(prev => {
          const next = { ...prev };
          delete next.street;
          delete next.neighborhood;
          delete next.city;
          delete next.state;
          return next;
        });
      } else {
        clearAddressFields();
        setErrors(prev => ({
          ...prev,
          zip_code: 'CEP não encontrado'
        }));
      }
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
      clearAddressFields();
      setErrors(prev => ({
        ...prev,
        zip_code: 'Erro ao buscar CEP'
      }));
    }
  };

  const handleCepChange = async (e) => {
    const { value } = e.target;
    
    setAddress(prev => ({
      ...prev,
      zip_code: value
    }));

    if (!value) {
      clearAddressFields();
      return;
    }

    if (validateCep(value)) {
      try {
        const response = await fetch(`https://viacep.com.br/ws/${value.replace(/\D/g, '')}/json/`);
        const data = await response.json();

        if (!data.erro) {
          setAddress(prev => ({
            ...prev,
            street: data.logradouro,
            neighborhood: data.bairro,
            city: data.localidade,
            state: data.uf,
            zip_code: value,
            complement: '',
            number: ''
          }));
          setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.zip_code;
            return newErrors;
          });
        } else {
          setAddress(prev => ({
            ...prev,
            street: '',
            neighborhood: '',
            city: '',
            state: '',
            complement: '',
            number: '',
            zip_code: value
          }));
          setErrors(prev => ({
            ...prev,
            zip_code: 'CEP não encontrado. Verifique o número digitado.'
          }));
        }
      } catch (error) {
        console.error('Erro ao buscar CEP:', error);
        setAddress(prev => ({
          ...prev,
          street: '',
          neighborhood: '',
          city: '',
          state: '',
          complement: '',
          number: '',
          zip_code: value
        }));
        setErrors(prev => ({
          ...prev,
          zip_code: 'Erro ao buscar CEP. Tente novamente.'
        }));
      }
    } else if (value.replace(/\D/g, '').length === 8) {
      setAddress(prev => ({
        ...prev,
        street: '',
        neighborhood: '',
        city: '',
        state: '',
        complement: '',
        number: '',
        zip_code: value
      }));
      setErrors(prev => ({
        ...prev,
        zip_code: 'CEP inválido. Verifique o número digitado.'
      }));
    }
  };

  const handleAddressInputChange = (e) => {
    const { name, value } = e.target;
    let nextValue = value;

    if (name === 'zip_code') {
      const numbersOnly = value.replace(/\D/g, '').slice(0, 8);
      nextValue = numbersOnly.length > 5
        ? `${numbersOnly.slice(0, 5)}-${numbersOnly.slice(5)}`
        : numbersOnly;

      const newAddress = { ...address, zip_code: nextValue };
      setAddress(newAddress);

      if (numbersOnly.length === 8) {
        setTimeout(() => {
          fetchAddress(numbersOnly);
        }, 0);
      }
    } else {
      setAddress({ ...address, [name]: nextValue });
    }

    const updated = { ...address, [name]: nextValue };
    const fieldErrors = validateAddress(updated);
    setErrors((prev) => {
      const next = { ...prev };
      if (fieldErrors[name]) {
        next[name] = fieldErrors[name];
      } else {
        delete next[name];
      }
      return next;
    });
  };

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#EDF2F5',
      borderRadius: '9.15px',
      transition: 'transform 0.2s ease-in-out',
      '& fieldset': {
        borderColor: 'transparent',
      },
      '&:hover': {
        transform: 'translateY(-2px)',
        '& fieldset': {
          borderColor: '#012F47',
          borderWidth: '1px',
        }
      },
      '&.Mui-focused': {
        transform: 'translateY(-2px)',
        '& fieldset': {
          borderColor: '#012F47',
          borderWidth: '1px',
        }
      },
    },
    '& label.Mui-focused': {
      color: '#536777',
    },
    '& .MuiInputLabel-root': {
      color: '#536777',
      fontSize: '0.9rem',
    },
    '& .MuiOutlinedInput-input': {
      color: '#012F47',
      padding: '14px 16px',
    },
    '& .MuiFormHelperText-root': {
      marginLeft: '4px',
      fontSize: '0.75rem',
    },
  };

  const validateAddress = (address) => {
    const errors = {};

    if (!address.zip_code) {
      errors.zip_code = 'CEP é obrigatório';
    } else {
      const numbersOnly = address.zip_code.replace(/\D/g, '');
      if (numbersOnly.length < 8) {
        errors.zip_code = 'CEP deve ter 8 dígitos';
      }
    }

    if (!address.street) {
      errors.street = 'Rua é obrigatória';
    }

    if (!address.number) {
      errors.number = 'Número é obrigatório';
    }

    if (!address.neighborhood) {
      errors.neighborhood = 'Bairro é obrigatório';
    }

    if (!address.city) {
      errors.city = 'Cidade é obrigatória';
    }

    if (!address.state) {
      errors.state = 'Estado é obrigatório';
    }

    return errors;
  };

  return (
    <Box sx={{ 
      ...commonBoxStyles,
      p: { xs: 5, sm: 5, md: 10 },
      pt: { xs: 5, sm: 5, md: 5 },
      mb: 5
    }}>
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: 3, md: 4 },
      }}>
        <Typography 
          variant="h1" 
          sx={{
            textAlign: 'center',
            fontWeight: 700,
            fontSize: { xs: '22px', md: '30px' },
            color: COLORS.primary,
          }}
        >
          Endereço do titular do cartão
        </Typography>

        <Grid 
          container 
          spacing={3}
          sx={{
            maxWidth: { xs: '100%', sm: '500px', md: '100%' },
            mx: 'auto'
          }}
        >
          <Grid item xs={12}>
            <TextField
              id="zip_code"
              name="zip_code"
              label="CEP"
              value={address.zip_code}
              onChange={handleCepChange}
              error={Boolean(errors.zip_code)}
              helperText={errors.zip_code}
              InputProps={{
                inputComponent: TextMaskZipCode,
                inputProps: {
                  name: 'zip_code'
                }
              }}
              fullWidth
              sx={textFieldSx}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              id="street"
              label="Rua"
              name="street"
              variant="outlined"
              sx={textFieldSx}
              value={address.street}
              error={Boolean(errors.street)}
              helperText={errors.street}
              onChange={handleAddressInputChange}
              fullWidth
              disabled={!address.zip_code}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              id="number"
              label="Número"
              name="number"
              variant="outlined"
              sx={textFieldSx}
              value={address.number}
              error={Boolean(errors.number)}
              helperText={errors.number}
              onChange={handleAddressInputChange}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={8}>
            <TextField
              id="complement"
              label="Complemento"
              name="complement"
              variant="outlined"
              sx={textFieldSx}
              value={address.complement}
              onChange={handleAddressInputChange}
              fullWidth
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              id="neighborhood"
              label="Bairro"
              name="neighborhood"
              variant="outlined"
              sx={textFieldSx}
              value={address.neighborhood}
              error={Boolean(errors.neighborhood)}
              helperText={errors.neighborhood}
              onChange={handleAddressInputChange}
              fullWidth
              disabled={!address.zip_code}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              id="city"
              label="Cidade"
              name="city"
              variant="outlined"
              sx={textFieldSx}
              value={address.city}
              error={Boolean(errors.city)}
              helperText={errors.city}
              onChange={handleAddressInputChange}
              fullWidth
              disabled={!address.zip_code}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              id="state"
              label="Estado"
              name="state"
              variant="outlined"
              sx={textFieldSx}
              value={address.state}
              error={Boolean(errors.state)}
              helperText={errors.state}
              onChange={handleAddressInputChange}
              inputProps={{ maxLength: 2 }}
              fullWidth
              disabled={!address.zip_code}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
});

AddressForm.propTypes = {
  address: PropTypes.shape({
    zip_code: PropTypes.string,
    street: PropTypes.string,
    number: PropTypes.string,
    complement: PropTypes.string,
    neighborhood: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string
  }).isRequired,
  setAddress: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  setErrors: PropTypes.func.isRequired,
};

export default AddressForm;